import draggable from 'vuedraggable';
import Component from 'vue-class-component';
import { Mixins, Prop } from 'vue-property-decorator';
import { COURSES_EVENTS } from '@/courses/values/courses-events.values';
import { PROJECT_COMMONS, SECTION_TYPES } from '@/content/values/sections';
import { SectionsProvider } from '@/content/providers/sections.provider'
import { SECTION_OPTION_MODE } from '@/courses/values/courses.values';
import EdSectionProjectsObject from '@/content/components/ed-section-projects-object/ed-section-projects-object.vue';
import EdSectionProjectsFolder from '../ed-section-projects-folder/ed-section-projects-folder.vue';
import { CreateObjectsMixin } from '@/courses/mixins/create-objects.mixin';
import { SCREEN_MIRRORING_EVENTS } from '@/values/screen.mirroring.values';
import { ProjectsMixin } from '@/courses/mixins/projects.mixin';
import { ContentType } from '@/content/types/content.types';

@Component({
  name: 'ed-section-projects',
  components: {
    draggable,
    EdSectionProjectsObject,
    EdSectionProjectsFolder
  }
})
export default class EdSectionProjects extends Mixins(CreateObjectsMixin, ProjectsMixin) {

  /* Props */
  @Prop() readonly parentId: number;
  @Prop() readonly level: number;
  @Prop({ default: 'SUBJECT' }) readonly contentType: ContentType;

  /* Data */
  public isOpened: boolean = false;
  public isSelected: boolean = false;
  public groupName: string = PROJECT_COMMONS.PROJECT_LIST;
  public cacheTimeout: any = null;
  public dropFilesVisible: any = false;

  /* Computed */
  get isMovingObject() {
    return this.$store.state.CourseStore.movingObject;
  }

  get projects() {
    return this.course.projects;
  }

  get hasSubProjects() {
    return this.project?.type == SECTION_TYPES.PROJECT.code;
  }

  get sublevel() {
    return this.level ? (this.level + 1) : 1;
  }

  get paddingLeft() {
    return `edg-pad-left--${(this.sublevel + 1) * 20}`;
  }

  get projectClasses() {
    return {
      'ed-section-projects--teacher-only' : this.project?.section?.teacherOnly || false,
      'ed-section-projects--opened': (this.isOpened && this.isParent),
      'ed-section-projects--not-opened': (!this.isOpened && this.isParent)
    }
  }

  get projectId() {
    return this.project.projectId;
  }

  get projectListId() {
    return `LIST${this.project.projectId}`;
  }

  get isParent() {
    return this.project?.type == SECTION_TYPES.PROJECT.code;
  }

  /* Methods */
  selectProject() {
    this.$edEventBus.$emit(COURSES_EVENTS.PROJECT_SELECTED, {
      projectId: this.project.projectId,
      courseId: this.courseID,
      classId: this.classID
    });
  }

  setSelected({projectId, courseId = -1 , classId}) {
    this.$edEventBus.$emit(COURSES_EVENTS.SECTION_SELECTED, {
      sectionId: 1,
      courseId: this.courseID,
      classId: this.classID
    });
    this.$edEventBus.$emit(COURSES_EVENTS.SECTION_SELECTED, {
      sectionId: null,
      courseId: this.courseID,
      classId: this.classID
    });
    if(this.options.selectable) {
      let classIdCondition: boolean = this.options.editable ? true : classId == this.classID
      if(this.courseID == courseId && classIdCondition) {
        this.isSelected = projectId == this.project.projectId;
      }
    }
  }

  openProject(emitMirroringEvent : boolean = true) {
    this.$edEventBus.$emit(COURSES_EVENTS.PROJECT_OPENED, { 
      projectId: this.project.projectId, 
      opened: !this.isOpened,
      courseId: this.courseID,
      classId: this.classID
    });

    if( emitMirroringEvent ) {
      var target = this.target(SCREEN_MIRRORING_EVENTS.ON_OPEN_PROJECT_FOLDER);
      this.$edEventBus.$emit(SCREEN_MIRRORING_EVENTS.CLICK, this, {target:target});
    }
  }

  checkChildren(project,projectId){
    if(!project || !project.projectId)
      return false;
    else if(project.projectId == projectId){
      return true;
    }
    else if(!project.projects){
      return false;
    }
    var result = false;
    project.projects.forEach(subProject=>{
      result = this.checkChildren(subProject,projectId) || result;
    });
    return result;
  }

  setOpened({ projectId, courseId = -1, opened , classId}) {
    if(this.$store.state.CourseStore.sectionOpenedMode == SECTION_OPTION_MODE.STRICT && !this.options.editable) {
      if(courseId == this.courseID && classId == this.classID) {
        if(projectId === this.project.projectId) {
          this.isOpened = opened;
        } else if(this.checkChildren(SectionsProvider.getProjectByID(this.projects, this.project.projectId), projectId)) {
          this.isOpened = true;
        } else {
          this.isOpened = false;
        }
      }
    }
    else {
      this.isOpened = projectId == this.project.projectId ? opened : this.isOpened;
    }
  }

  unselectProjects() {
    this.isSelected = null;
  }

  goToProject() {
    this.selectProject();
    if(this.options.editable) {
      this.goToEditProject();
    } else {
      this.setFolderInStorage(this.project.projectId, this.parentId);
      let data = { 
        projectId: this.project.projectId,
        courseId: this.courseID,
        classId: this.classID
      };
      if(this.project.type !== SECTION_TYPES.PROJECT.code) {
        this.goToProjectPreview(data);
      } else {
        this.setTeacherPosition({
          sectionId: this.projectId,
          classId: this.classID,
          courseId: this.courseID,
          isFolder: true
        });
      }
    }
  }

  async sectionMoved(e) {
    this.$Courses.Store.setMovingObject(false);

    /* Added into this section */
    if(e.added) {
      this.sectionAdded(e);
    }

    /* Moved inside of the section */ 
    if(e.moved) {
      this.$Courses.Store.reorderProjectA({
        project: e.moved.element,
        parentId: this.project.projectId,
        place: e.moved.newIndex == 0 ? -1 : this.project.projects[(e.moved.newIndex - 1)] ? this.project.projects[(e.moved.newIndex - 1)].projectId : this.project.projects[e.moved.newIndex].projectId,
        courseId: this.courseID
      });
    }
  }

  async sectionAdded(e) {
    await this.$Courses.Store.reorderProjectA({ 
      project: e.added.element,
      parentId: this.project.projectId,
      place: e.added.newIndex == 0 ? -1 : this.project.projects[e.added.newIndex] ? this.project.projects[e.added.newIndex].projectId : -1,
      courseId: this.courseID
    });
  }

  setFolderOpened(data: any) {
    if(data.sectionSelected) {
      let included = data.listIds.includes(this.projectId);
      if(!included) this.isOpened = false;
      return;
    }
    this.isOpened = false;
  }

  target(screenMirroringEventName:string) {
    return screenMirroringEventName.replace('{projectId}', this.project.projectId.toString());
  }

  /* Hooks */
  created() {
    this.$edEventBus.$on(COURSES_EVENTS.PROJECT_OPENED, this.setOpened);
    this.$edEventBus.$on(COURSES_EVENTS.PROJECT_SELECTED, this.setSelected);
    this.$edEventBus.$on(COURSES_EVENTS.KEEP_OPEN_FOLDERS, this.setFolderOpened);
    this.$edEventBus.$on(COURSES_EVENTS.UNSELECT_PROJECTS, this.unselectProjects);
    this.$edEventBus.$on(this.target(SCREEN_MIRRORING_EVENTS.ON_OPEN_PROJECT_FOLDER), (args:any) => {
      this.openProject(false);
    });
  }

  beforeDestroy(): void {
    this.$edEventBus.$off(this.target(SCREEN_MIRRORING_EVENTS.ON_OPEN_PROJECT_FOLDER));
  }
}
